import React, { useEffect, useState, useRef } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import IconCross from '../atoms/icon-cross';
import Button from '../atoms/button';

import useEventListener from './hooks/useEventListener';
import { Page, Content } from '../theme/page';
import manageFocus from './manageFocus';

const Modal = styled(Page)`
  animation: ${({ theme }) => theme.animations.fadeInFromNothing} ${({ theme }) => theme.animation.default};
  bottom: 0;
  display: ${({ active }) => (active ? 'block' : 'none')};
  left: 0;
  opacity: ${({ active }) => (active ? '1' : '0')};
  overflow: hidden;
  position: fixed;
  right: 0;
  top: 0;
  transition: opacity ${(props) => props.theme.animation.default} ${(props) => props.theme.animation.fast};
  z-index: ${({ theme }) => theme.layers.box};
`;

const Close = styled(Button)`
  border: 0;
  display: flex;
  height: 4rem;
  margin: 0.5rem 0.5em 0.5rem auto;
  max-height: 4rem;
  max-width: 4rem;
  position: sticky;
  top: 0.5rem;
  width: 4rem;
  z-index: ${({ theme }) => theme.layers.box};

  svg {
    fill: transparent;
    height: 4rem;
    stroke-width: 5;
    stroke: ${(props) => (props.styling === 'dark-opaque' ? props.theme.color.white : props.theme.color.brand)};
    width: 4rem;
    padding: 0.5rem;
  }
`;

const asModal = (WrappedComponent) => {
  const Component = (props) => {
    const [prevElement, setPrevElement] = useState(null);
    const modalRef = useRef();

    useEffect(() => {
      global.document.body.style.overflowY = props.active ? 'hidden' : '';

      const { current } = modalRef;
      const previousElement = global.document.activeElement;
      if (props.active) setPrevElement(previousElement);
      manageFocus(current, props.active, prevElement);

      current.style.overflow = props.active ? 'auto' : 'hidden';
    }, [props.active]);

    const handleEscKeypress = ({ code }) => {
      if (code !== 'Escape') return;
      props.onClose();
    };

    useEventListener('keyup', handleEscKeypress);

    return (
      <Modal ref={modalRef} background={props?.styling ?? 'default'} active={props.active}>
        <Close id="close-modal" aria-label="close modal" type="button" styling={props?.styling} inverted outline isIcon onClick={props.onClose}>
          <IconCross />
        </Close>

        <Content>
          <WrappedComponent {...props} />
        </Content>
      </Modal>
    );
  };

  Component.defaultProps = {
    active: false
  };

  Component.propTypes = {
    active: PropTypes.bool,
    onClose: PropTypes.func.isRequired
  };

  return Component;
};

export default asModal;
